import React, { useEffect, useRef } from 'react';
import { ShaderController, ShaderCue, ShaderTheme, DEFAULT_SHADER_CUE } from '../../types/shader';
import { ShaderRegistry, ShaderUniforms, ShaderRenderContext } from './shaders/index';

export const UNIFORM_INTERPOLATION_DURATION = 1.2; // 1.2 seconds

/**
 * Singleton Canvas Engine
 *
 * Recycles a single Canvas context across all 500 pages, eliminating WebGL/2D
 * context leaks. Handles 60fps render loop, window resizing with DPR clamped to 2.0,
 * document visibility throttling, and smooth 1.2s cubic ease-out uniform interpolation.
 */
export class CanvasEngine implements ShaderController {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private readonly registry: ShaderRegistry;

  private activeTheme: ShaderTheme = DEFAULT_SHADER_CUE.theme;
  private previousTheme: ShaderTheme = DEFAULT_SHADER_CUE.theme;

  // Uniform interpolation states
  private currentUniforms: ShaderUniforms = {
    speed: DEFAULT_SHADER_CUE.speed,
    intensity: DEFAULT_SHADER_CUE.intensity,
    distortion: DEFAULT_SHADER_CUE.distortion,
    gridDensity: DEFAULT_SHADER_CUE.gridDensity,
  };

  private startUniforms: ShaderUniforms = { ...this.currentUniforms };
  private targetUniforms: ShaderUniforms = { ...this.currentUniforms };
  private targetCue: ShaderCue = { ...DEFAULT_SHADER_CUE };

  private transitionStartTime = 0;
  private transitionDuration = UNIFORM_INTERPOLATION_DURATION;
  private transitionProgress = 1.0;

  // Viewport metrics
  private width = 800;
  private height = 600;
  private dpr = 1.0;

  // Animation and lifecycle flags
  private rafId: number | null = null;
  private lastTimestamp = 0;
  private elapsedTime = 0;
  private paused = false;
  private throttled = false;
  private reducedMotion = false;

  // Listeners for cleanup
  private resizeHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor() {
    this.registry = new ShaderRegistry();
  }

  attachCanvas(canvas: HTMLCanvasElement): void {
    if (this.canvas === canvas && this.ctx) {
      return;
    }

    this.canvas = canvas;
    // Acquire context once and recycle
    const context = canvas.getContext('2d');
    this.ctx = context;

    this.setupListeners();
    this.updateDimensions();
    this.resume();
  }

  detachCanvas(canvas?: HTMLCanvasElement): void {
    if (canvas && this.canvas !== canvas) {
      return;
    }
    this.teardownListeners();
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.canvas = null;
    this.ctx = null;
  }

  private setupListeners(): void {
    this.teardownListeners();

    if (typeof window !== 'undefined') {
      this.resizeHandler = () => this.updateDimensions();
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    }

    if (typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (document.hidden) {
          this.throttled = true;
        } else {
          this.throttled = false;
          this.lastTimestamp = typeof performance !== 'undefined' ? performance.now() : Date.now();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  private teardownListeners(): void {
    if (typeof window !== 'undefined' && this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    if (typeof document !== 'undefined' && this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private updateDimensions(): void {
    if (typeof window === 'undefined') return;
    const w = window.innerWidth || 800;
    const h = window.innerHeight || 600;
    const rawDpr = window.devicePixelRatio || 1.0;
    this.resize(w, h, rawDpr);
  }

  /**
   * Adapts canvas dimensions and clamps DPI to max 2.0 to protect GPU memory
   */
  resize(width: number, height: number, dpr?: number): void {
    this.width = Math.max(1, Math.floor(width));
    this.height = Math.max(1, Math.floor(height));

    // Clamp devicePixelRatio to range [1.0, 2.0]
    const rawDpr = dpr !== undefined ? dpr : (typeof window !== 'undefined' ? window.devicePixelRatio : 1.0) || 1.0;
    this.dpr = Math.min(2.0, Math.max(1.0, rawDpr));

    if (this.canvas) {
      this.canvas.width = Math.floor(this.width * this.dpr);
      this.canvas.height = Math.floor(this.height * this.dpr);
      this.canvas.style.width = `${this.width}px`;
      this.canvas.style.height = `${this.height}px`;
    }
  }

  setReducedMotion(enabled: boolean): void {
    this.reducedMotion = enabled;
  }

  /**
   * Smooth 1.2s interpolation for shader uniforms (speed, intensity, distortion, gridDensity)
   */
  updateShaderCue(cue: ShaderCue, immediate = false): void {
    // Clamp incoming parameters to safe valid ranges
    const clampedCue: ShaderCue = {
      theme: cue.theme,
      speed: Math.max(0.1, Math.min(3.0, cue.speed)),
      intensity: Math.max(0.0, Math.min(1.0, cue.intensity)),
      distortion: Math.max(0.0, Math.min(1.0, cue.distortion)),
      gridDensity: Math.max(10, Math.min(80, cue.gridDensity)),
    };

    this.targetCue = { ...clampedCue };

    if (immediate) {
      this.activeTheme = clampedCue.theme;
      this.previousTheme = clampedCue.theme;
      this.currentUniforms = {
        speed: clampedCue.speed,
        intensity: clampedCue.intensity,
        distortion: clampedCue.distortion,
        gridDensity: clampedCue.gridDensity,
      };
      this.startUniforms = { ...this.currentUniforms };
      this.targetUniforms = { ...this.currentUniforms };
      this.transitionProgress = 1.0;
      return;
    }

    // Capture current snapshot as starting point
    this.startUniforms = { ...this.currentUniforms };
    this.targetUniforms = {
      speed: clampedCue.speed,
      intensity: clampedCue.intensity,
      distortion: clampedCue.distortion,
      gridDensity: clampedCue.gridDensity,
    };

    if (clampedCue.theme !== this.activeTheme) {
      this.previousTheme = this.activeTheme;
      this.activeTheme = clampedCue.theme;
    }

    const now = typeof performance !== 'undefined' ? performance.now() / 1000 : Date.now() / 1000;
    this.transitionStartTime = now;
    this.transitionDuration = UNIFORM_INTERPOLATION_DURATION;
    this.transitionProgress = 0.0;
  }

  private updateInterpolation(currentTimeSec: number): void {
    if (this.transitionProgress >= 1.0) {
      return;
    }

    const elapsed = currentTimeSec - this.transitionStartTime;
    const rawProgress = Math.min(1.0, Math.max(0.0, elapsed / this.transitionDuration));

    // Smooth cubic ease-out curve: 1 - (1 - p)^3
    const eased = 1.0 - Math.pow(1.0 - rawProgress, 3);
    this.transitionProgress = eased;

    this.currentUniforms.speed =
      this.startUniforms.speed + (this.targetUniforms.speed - this.startUniforms.speed) * eased;
    this.currentUniforms.intensity =
      this.startUniforms.intensity + (this.targetUniforms.intensity - this.startUniforms.intensity) * eased;
    this.currentUniforms.distortion =
      this.startUniforms.distortion + (this.targetUniforms.distortion - this.startUniforms.distortion) * eased;
    this.currentUniforms.gridDensity =
      this.startUniforms.gridDensity + (this.targetUniforms.gridDensity - this.startUniforms.gridDensity) * eased;

    if (rawProgress >= 1.0) {
      this.transitionProgress = 1.0;
      this.previousTheme = this.activeTheme;
      this.currentUniforms = { ...this.targetUniforms };
    }
  }

  renderFrame(manualDt?: number): void {
    if (!this.ctx) return;

    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const dt = manualDt !== undefined
      ? manualDt
      : this.lastTimestamp > 0
      ? (now - this.lastTimestamp) / 1000
      : 0.016;

    this.lastTimestamp = now;
    this.elapsedTime += dt;

    const nowSec = now / 1000;
    this.updateInterpolation(nowSec);

    const ctx = this.ctx;
    ctx.save();
    // Scale coordinate space by DPR so shaders work in logical CSS pixels
    ctx.scale(this.dpr, this.dpr);

    const renderContext: ShaderRenderContext = {
      ctx,
      width: this.width,
      height: this.height,
      dpr: this.dpr,
      time: this.elapsedTime,
      deltaTime: dt,
      uniforms: this.currentUniforms,
      reducedMotion: this.reducedMotion,
    };

    // Cross-fade rendering if theme transition is active
    if (this.transitionProgress < 1.0 && this.previousTheme !== this.activeTheme) {
      const prevModule = this.registry.get(this.previousTheme);
      const activeModule = this.registry.get(this.activeTheme);

      // Render outgoing previous theme at (1.0 - progress) opacity
      ctx.save();
      ctx.globalAlpha = Math.max(0, 1.0 - this.transitionProgress);
      prevModule.render(renderContext);
      ctx.restore();

      // Render incoming active theme at progress opacity
      ctx.save();
      ctx.globalAlpha = Math.min(1, this.transitionProgress);
      activeModule.render(renderContext);
      ctx.restore();
    } else {
      const activeModule = this.registry.get(this.activeTheme);
      activeModule.render(renderContext);
    }

    ctx.restore();
  }

  private loop = (): void => {
    if (this.paused) return;

    if (!this.throttled) {
      this.renderFrame();
    }

    if (!this.paused) {
      this.rafId = requestAnimationFrame(this.loop);
    }
  };

  pause(): void {
    this.paused = true;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  resume(): void {
    if (!this.paused && this.rafId !== null) {
      return;
    }
    this.paused = false;
    this.lastTimestamp = typeof performance !== 'undefined' ? performance.now() : Date.now();
    this.rafId = requestAnimationFrame(this.loop);
  }

  destroy(): void {
    this.pause();
    this.teardownListeners();
    this.registry.resetAll();
    this.canvas = null;
    this.ctx = null;
  }

  // Introspection & Testing helpers
  getActiveTheme(): ShaderTheme {
    return this.activeTheme;
  }

  getPreviousTheme(): ShaderTheme {
    return this.previousTheme;
  }

  getCurrentCue(): ShaderCue {
    return {
      theme: this.activeTheme,
      speed: this.currentUniforms.speed,
      intensity: this.currentUniforms.intensity,
      distortion: this.currentUniforms.distortion,
      gridDensity: this.currentUniforms.gridDensity,
    };
  }

  getTargetCue(): ShaderCue {
    return { ...this.targetCue };
  }

  getInterpolatedUniforms(): ShaderUniforms & { transitionProgress: number } {
    return {
      ...this.currentUniforms,
      transitionProgress: this.transitionProgress,
    };
  }

  getDpr(): number {
    return this.dpr;
  }

  isPaused(): boolean {
    return this.paused;
  }

  isThrottled(): boolean {
    return this.throttled;
  }

  getCanvas(): HTMLCanvasElement | null {
    return this.canvas;
  }

  getContext(): CanvasRenderingContext2D | null {
    return this.ctx;
  }
}

// Global shared singleton engine instance
let sharedCanvasEngineInstance: CanvasEngine | null = null;

export function getSharedCanvasEngine(): CanvasEngine {
  if (!sharedCanvasEngineInstance) {
    sharedCanvasEngineInstance = new CanvasEngine();
  }
  return sharedCanvasEngineInstance;
}

export interface CanvasBackdropProps {
  cue?: ShaderCue;
  activeTheme?: ShaderTheme;
  className?: string;
  style?: React.CSSProperties;
  onControllerReady?: (controller: ShaderController) => void;
  reducedMotion?: boolean;
}

/**
 * Persistent Singleton Canvas Backdrop Component
 */
export const CanvasBackdrop: React.FC<CanvasBackdropProps> = ({
  cue,
  activeTheme,
  className = '',
  style,
  onControllerReady,
  reducedMotion = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<CanvasEngine>(getSharedCanvasEngine());

  // Mount & Singleton Attachment
  useEffect(() => {
    const engine = engineRef.current;
    if (canvasRef.current) {
      engine.attachCanvas(canvasRef.current);
      if (onControllerReady) {
        onControllerReady(engine);
      }
    }

    return () => {
      // Do not destroy engine across page mounts to preserve singleton context
      // Simply pause if unmounting completely
    };
  }, [onControllerReady]);

  // Reduced motion preference
  useEffect(() => {
    engineRef.current.setReducedMotion(reducedMotion);
  }, [reducedMotion]);

  // Reactive Cue updates
  useEffect(() => {
    const engine = engineRef.current;
    if (cue) {
      engine.updateShaderCue(cue);
    } else if (activeTheme) {
      engine.updateShaderCue({
        ...DEFAULT_SHADER_CUE,
        theme: activeTheme,
      });
    }
  }, [cue, activeTheme]);

  return (
    <canvas
      ref={canvasRef}
      className={`caesura-canvas-backdrop ${className}`}
      data-testid="caesura-canvas-backdrop"
      data-active-theme={cue ? cue.theme : activeTheme || DEFAULT_SHADER_CUE.theme}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -10,
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
};

export default CanvasBackdrop;
